import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { ChevronRight, ShieldCheck } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export default function PrivacyPolicy() {
    return (
        <LandingLayout>
            <SeoHead title="Privacy Policy" description="Privacy policy for Northeastern College's website." image={`${baseUrl}/images/logo.webp`} />

            <section id="privacy-policy" className="bg-muted pb-16">
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/privacy.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Privacy Policy</span>
                        </div>

                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Privacy Policy
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Learn how Northeastern College handles website information, privacy practices, and data protection.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="bg-card border-border overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-10">
                        <div className="mb-8 rounded-2xl border border-emerald-500/15 bg-emerald-500/5 px-5 py-4">
                            <p className="text-sm font-semibold tracking-wide text-[#0A7A35] uppercase">Last Updated</p>
                            <p className="text-foreground mt-1 text-lg font-bold">January 24, 2026</p>
                        </div>
                        <p className="text-muted-foreground mb-6 text-sm leading-relaxed md:text-base">
                            This Privacy Policy explains how Northeastern College ("we", "our", "us") collects, uses, and protects personal
                            information when you access our official website or interact with our online services.
                        </p>
                        <p className="text-muted-foreground mb-8 text-sm leading-relaxed md:text-base">
                            By using our website, you agree to the practices described in this policy.
                        </p>

                        <div className="text-muted-foreground space-y-6 text-sm leading-relaxed md:text-base">
                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">1. Information We Collect</h3>
                                <p className="mb-3">
                                    We only collect information that is necessary for basic website functionality, visitor safety, and newsletter
                                    communication.
                                </p>
                                <div className="space-y-3">
                                    <div>
                                        <div className="text-foreground font-semibold">a. Information You Provide</div>
                                        <div>Email address - collected when users subscribe to newsletters or updates.</div>
                                    </div>
                                    <div>
                                        <div className="text-foreground font-semibold">b. Information Collected Automatically</div>
                                        <div className="mb-2">
                                            We may automatically collect limited technical data when you visit the site, including:
                                        </div>
                                        <ul className="list-disc space-y-1 pl-5">
                                            <li>IP address - used for internal visitor logs and security monitoring.</li>
                                            <li>Browser type and device information</li>
                                            <li>Pages viewed and visit timestamps</li>
                                        </ul>
                                        <div className="mt-2">
                                            All automatic data is used only for analytics, security, and improving user experience.
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-foreground font-semibold">c. Login Information</div>
                                        <div>Only administrators and authorized staff have accounts.</div>
                                        <div>No public user accounts are provided.</div>
                                    </div>
                                    <div>
                                        <div className="text-foreground font-semibold">d. AI Chatbot</div>
                                        <div>We host an AI chatbot for convenience.</div>
                                        <div>It does not save, store, or collect any user messages or personal data.</div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">2. How We Use the Information</h3>
                                <div className="mb-2">We use collected data for the following purposes:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>To send newsletters and announcements (when subscribed)</li>
                                    <li>To monitor website performance</li>
                                    <li>To maintain visitor logs for campus network security</li>
                                    <li>To ensure proper operation of administrative features</li>
                                    <li>To improve website content and functionality</li>
                                </ul>
                                <div className="mt-2">We do not sell, trade, or share personal data with external marketing companies.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">3. Cookies and Tracking</h3>
                                <div className="mb-2">Our website uses essential cookies only, specifically for:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Visitor logging and analytics</li>
                                    <li>Basic site functionality</li>
                                </ul>
                                <div className="mt-2">
                                    We do not use tracking tools like Facebook Pixel or Google Analytics unless explicitly stated.
                                </div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">4. Who Has Access to the Information</h3>
                                <div className="mb-2">Limited data may be accessed by:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Authorized administrators</li>
                                    <li>IT personnel responsible for site maintenance</li>
                                    <li>Third-party service providers (only if needed for hosting or security)</li>
                                </ul>
                                <div className="mt-2">All personnel follow strict confidentiality requirements.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">5. Data Security</h3>
                                <div className="mb-2">We implement technical and organizational measures such as:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Secure hosting</li>
                                    <li>HTTPS encryption</li>
                                    <li>Access controls for administrators</li>
                                </ul>
                                <div className="mt-2">While no system is 100% secure, we strive to protect all data under our care.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">6. Children's Privacy</h3>
                                <div>
                                    Our website may be accessed by upcoming freshmen, senior high school students, and individuals under 18. We do not
                                    knowingly collect personal information from minors except for newsletter subscriptions, which require voluntary
                                    input.
                                </div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">7. Your Rights</h3>
                                <div className="mb-2">Depending on applicable local policies, you may:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Request access to your information</li>
                                    <li>Request deletion of your email from our mailing list</li>
                                    <li>Opt-out of newsletters at any time</li>
                                </ul>
                                <div className="mt-2">You may contact us at support@northeasterncollege.edu.ph for any data-related concerns.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">8. Changes to This Privacy Policy</h3>
                                <div>We may update this policy periodically. Changes will be posted on this page with a new "Last Updated" date.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">9. Contact Information</h3>
                                <div>For questions or concerns about our Privacy Policy:</div>
                                <div className="mt-1">support@northeasterncollege.edu.ph</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
